import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useBackNavigation } from '../../hooks/useBackNavigation.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface SupplierAddress {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postal_code?: string | null;
}

interface SupplierDetail {
  _id: string;
  company_name: string;
  email: string;
  phone?: string | null;
  address?: SupplierAddress | null;
  logo_file_asset_id?: string | null;
  logo_url?: string | null;
  status: string;
  currency_id?: string | null;
}

interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
}

interface FormState {
  company_name: string;
  email: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  currency_id: string;
}

const EMPTY_FORM: FormState = {
  company_name: '',
  email: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  currency_id: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Trim; treat an emptied field as an explicit clear (null → backend $unset). */
function toNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function SupplierProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/suppliers');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [status, setStatus] = useState('active');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // undefined = no local override yet (show the server's logo_url); a string or
  // explicit null = the just-uploaded/cleared logo, which wins over the query.
  const [uploadedLogo, setUploadedLogo] = useState<string | null | undefined>(undefined);

  const { data: supplier } = useQuery({
    queryKey: RESOURCE_KEYS.supplier.one(id as string),
    queryFn: () => api.get<SupplierDetail>(`/suppliers/${id}`),
    enabled: Boolean(id),
  });

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.get<Currency[]>('/currencies'),
  });

  useEffect(() => {
    if (!supplier) return;
    const a = supplier.address ?? {};
    setForm({
      company_name: supplier.company_name ?? '',
      email: supplier.email ?? '',
      phone: supplier.phone ?? '',
      line1: a.line1 ?? '',
      line2: a.line2 ?? '',
      city: a.city ?? '',
      state: a.state ?? '',
      country: a.country ?? '',
      postal_code: a.postal_code ?? '',
      currency_id: supplier.currency_id ?? '',
    });
    setStatus(supplier.status ?? 'active');
  }, [supplier]);

  const logoUrl = uploadedLogo !== undefined ? uploadedLogo : (supplier?.logo_url ?? null);

  // The next status the confirmation would apply: active suppliers get suspended
  // (staff sign-in is blocked); suspended ones get reactivated.
  const nextStatus = status === 'active' ? 'suspended' : 'active';

  const statusMutation = useMutation({
    mutationFn: () => api.patch<SupplierDetail>(`/suppliers/${id}`, { status: nextStatus }),
    onSuccess: (updated) => {
      setStatus(updated.status);
      setConfirmOpen(false);
    },
    onError: (err: { message?: string }) =>
      setSubmitError(err.message ?? 'Failed to change supplier status'),
  });

  const logoMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'supplier_logo');
      const asset = (await api.upload('/files/upload', fd)) as { id: string };
      return api.patch<SupplierDetail>(`/suppliers/${id}`, { logo_file_asset_id: asset.id });
    },
    onSuccess: (updated) => {
      setUploadedLogo(updated.logo_url ?? null);
    },
    onError: (err: { message?: string }) => setSubmitError(err.message ?? 'Failed to upload logo'),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch<SupplierDetail>(`/suppliers/${id}`, payload),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.supplier.one(id as string) });
      // 161: found by T026's sweep, not by the spec's original tables — this screen refreshed the
      // supplier but not the list, so the Suppliers list kept the old company name.
      queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.supplier.list });
    },
    onError: (err: { message?: string }) =>
      setSubmitError(err.message ?? 'Failed to save supplier profile'),
  });

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (form.company_name.trim() === '') next.company_name = 'Company name is required.';
    // 364 (#1217): optional, but checked when given.
    if (form.email.trim() !== '' && !EMAIL_RE.test(form.email.trim()))
      next.email = 'Enter a valid email address.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    setSaved(false);
    if (!validate()) return;
    mutate({
      company_name: form.company_name.trim(),
      email: toNullable(form.email),
      phone: toNullable(form.phone),
      address: {
        line1: toNullable(form.line1),
        line2: toNullable(form.line2),
        city: toNullable(form.city),
        state: toNullable(form.state),
        country: toNullable(form.country),
        postal_code: toNullable(form.postal_code),
      },
      currency_id: form.currency_id === '' ? null : form.currency_id,
    });
  }

  return (
    <div>
      <PageHeader
        title="Supplier Profile"
        description={
          supplier ? `Manage the profile for ${supplier.company_name}.` : 'Manage the supplier.'
        }
        fallback="/suppliers"
      />
      <form className="max-w-2xl space-y-6" onSubmit={onSubmit} noValidate>
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Business details</h2>
          <div className="space-y-4">
            <FormField
              label="Company Name"
              name="company_name"
              required
              error={errors.company_name}
            >
              <Input
                id="company_name"
                value={form.company_name}
                onInput={(e) => update('company_name', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Email" name="email" optional error={errors.email}>
              <Input
                id="email"
                type="email"
                value={form.email}
                onInput={(e) => update('email', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Phone" name="phone">
              <Input
                id="phone"
                value={form.phone}
                onInput={(e) => update('phone', (e.target as HTMLInputElement).value)}
              />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Address</h2>
          <div className="space-y-4">
            <FormField label="Address line 1" name="line1">
              <Input
                id="line1"
                value={form.line1}
                onInput={(e) => update('line1', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Address line 2" name="line2">
              <Input
                id="line2"
                value={form.line2}
                onInput={(e) => update('line2', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="City" name="city">
              <Input
                id="city"
                value={form.city}
                onInput={(e) => update('city', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="State" name="state">
              <Input
                id="state"
                value={form.state}
                onInput={(e) => update('state', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Country" name="country">
              <Input
                id="country"
                value={form.country}
                onInput={(e) => update('country', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Postal code" name="postal_code">
              <Input
                id="postal_code"
                value={form.postal_code}
                onInput={(e) => update('postal_code', (e.target as HTMLInputElement).value)}
              />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Logo</h2>
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Supplier logo"
                className="h-16 w-16 rounded object-contain border border-hairline bg-canvas"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-hairline text-xs text-steel">
                None
              </div>
            )}
            <FormField label="Logo" name="logo" hint="PNG, JPG or WebP up to 10 MB.">
              <input
                id="logo"
                type="file"
                accept="image/*"
                disabled={logoMutation.isPending}
                className="text-sm text-slate"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) logoMutation.mutate(file);
                }}
              />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Status</h2>
          <div className="flex items-center gap-4">
            <StatusBadge status={status} />
            <Button
              type="button"
              variant={status === 'active' ? 'danger' : 'primary'}
              onClick={() => setConfirmOpen(true)}
            >
              {status === 'active' ? 'Suspend' : 'Reactivate'}
            </Button>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Currency</h2>
          <div className="space-y-4">
            <FormField label="Currency" name="currency_id">
              <Select
                id="currency_id"
                value={form.currency_id}
                onChange={(e) => update('currency_id', (e.target as HTMLSelectElement).value)}
              >
                <option value="">— None —</option>
                {(currencies ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </Select>
            </FormField>
          </div>
        </Card>

        {submitError && <p className="text-sm text-red-600">{submitError}</p>}
        {saved && <p className="text-sm text-green-600">Profile saved.</p>}

        <div className="flex gap-3">
          <Button type="submit" loading={isPending}>
            {isPending ? 'Saving…' : 'Save Changes'}
          </Button>
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        </div>
      </form>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={status === 'active' ? 'Suspend supplier' : 'Reactivate supplier'}
      >
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-slate">
            {status === 'active'
              ? 'Suspending this supplier will block its staff — they will be unable to sign in until it is reactivated.'
              : 'Reactivating this supplier will restore its staff’s ability to sign in.'}
          </p>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={status === 'active' ? 'danger' : 'primary'}
              loading={statusMutation.isPending}
              onClick={() => statusMutation.mutate()}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
